import moment from "moment";
import { identifiersConfig } from "./identifiersConfig";
import elementScoreHandler from "./elementScoreExtractUtil";
import { formattedDateWithDefault } from "../utils/formattedDate";
import { quarantineClassificationsMap } from "./constants";
import { recordHasOverride } from "../routes/NewSearchRecords/SearchRecordTable/GoldenRecordExclusion/Utils";
import {
  overriddenByString,
  overrideReasonString,
  overrideTimestampString,
  dobOverriddenByString,
  dobOverrideReasonString,
  dobOverrideTimestampString,
  genderOverriddenByString,
  genderOverrideReasonString,
  genderOverrideTimestampString,
  multipleBirthOverriddenByString,
  multipleBirthOverrideReasonString,
  multipleBirthOverrideTimestampString
} from "../routes/NewSearchRecords/SearchRecordTable/GoldenRecordExclusion/Utils";

const identifiers = identifiersConfig.map(identifier => ({
  name: identifier.heading.toLowerCase(),
  system: identifier.system ? identifier.system : "",
  code: identifier.code ? identifier.code : ""
}));

const identifierHandler = (identifier, systemString, codeString) => {
  if (!identifier) return "-";
  let identifierObj;
  if (systemString !== "" && codeString !== "") {
    identifierObj = identifier.findLast(
      ({ type, system, use, extension }) =>
        type &&
        type.coding &&
        type.coding[0].code === codeString &&
        system === systemString &&
        use !== "old" &&
        !(
          extension &&
          extension.some(ext => ext.url === "additionalDataFromRecords")
        )
    );
  } else if (systemString === "" && codeString !== "") {
    identifierObj = identifier.findLast(
      ({ type, use, extension }) =>
        type &&
        type.coding &&
        type.coding[0].code === codeString &&
        use !== "old" &&
        !(
          extension &&
          extension.some(ext => ext.url === "additionalDataFromRecords")
        )
    );
  } else if (systemString !== "" && codeString === "") {
    identifierObj = identifier.findLast(
      ({ system, use, extension }) =>
        system === systemString &&
        use !== "old" &&
        !(
          extension &&
          extension.some(ext => ext.url === "additionalDataFromRecords")
        )
    );
  }
  return identifierObj
    ? identifierObj.value
      ? identifierObj.value
      : "-"
    : "-";
};

export const fullNameHandler = name => {
  const suffixName = name.suffix ? name.suffix.join(" ") : "";
  const givenName = name.given ? name.given.join(" ") : "";
  const lastName = name.family ? name.family : "";
  return `${lastName}${
    lastName && givenName && suffixName ? ", " : ""
  } ${givenName} ${suffixName}`;
};

export const addressHandler = address => {
  const line = address.line ? address.line.join(", ") : "";
  const city = address.city ? address.city : "";
  const state = address.state ? address.state : "";
  const postalCode = address.postalCode ? address.postalCode : "";
  return `${line} ${city} ${state} ${postalCode}`;
};

const emailHandler = telecom => {
  let emailObj = telecom.findLast(
    ({ system, value, use }) =>
      system === "email" &&
      value &&
      use !== "old" &&
      !(
        telecom.extension &&
        telecom.extension.some(ext => ext.url === "additionalDataFromRecords")
      )
  );
  if (!emailObj)
    emailObj = telecom.findLast(
      ({ system, use }) => system === "email" && use !== "old"
    );

  const emailUse = emailObj && emailObj.use ? emailObj.use : "-";
  const emailValue = emailObj && emailObj.value ? emailObj.value : "-";
  return emailObj ? [emailValue, emailUse] : "-";
};

const emailCountHandler = telecom => {
  const emailObj = telecom.filter(({ system }) => system === "email");
  return emailObj ? emailObj.length : 0;
};

const phoneNumberHandler = telecom => {
  let phoneObj = telecom.findLast(
    ({ system, value, use }) =>
      system === "phone" &&
      value &&
      use !== "old" &&
      !(
        telecom.extension &&
        telecom.extension.some(ext => ext.url === "additionalDataFromRecords")
      )
  );
  if (!phoneObj)
    phoneObj = telecom.findLast(
      ({ system, use }) => system === "phone" && use !== "old"
    );

  const phoneUse = phoneObj && phoneObj.use ? phoneObj.use : "-";
  const phoneValue = phoneObj && phoneObj.value ? phoneObj.value : "-";
  return phoneObj ? [phoneValue, phoneUse] : "-";
};

const phoneNumberCountHandler = telecom => {
  const phoneObj = telecom.filter(({ system }) => system === "phone");
  return phoneObj ? phoneObj.length : 0;
};

const nameHandler = names => {
  if (!Array.isArray(names) || names.length === 0) {
    return ["-", "-", "-"];
  }

  const isEligible = n =>
    (n?.suffix || n?.given || n?.family) &&
    n?.use !== "old" &&
    Array.isArray(n?.extension);

  const getAttrCode = n =>
    n?.extension?.find(ext => String(ext.url).includes("attrCode"))
      ?.valueString;

  let chosen =
    names.find(n => isEligible(n) && getAttrCode(n) === "PERPREFNAME") ||
    names.find(n => isEligible(n) && getAttrCode(n) === "PERCHSNNAME") ||
    names
      .slice(0)
      .reverse()
      .find(isEligible) ||
    names[names.length - 1];

  const suffixName =
    Array.isArray(chosen?.suffix) && chosen.suffix.length
      ? chosen.suffix.join(", ")
      : "-";

  const givenName =
    Array.isArray(chosen?.given) && chosen.given.length
      ? chosen.given
          .map((g, i) => (chosen.given[i + 1] ? g + ", " : g))
          .join("")
      : "-";

  const familyName = chosen?.family || "-";

  return [suffixName, givenName, familyName];
};

const multipleAddressHandler = address => {
  let addressObj = address.findLast(
    ({ line, city, state, postalCode, use }) =>
      (line || city || state || postalCode) &&
      use !== "old" &&
      !(
        address.extension &&
        address.extension.some(ext => ext.url === "additionalDataFromRecords")
      )
  );
  const addressLine =
    addressObj && addressObj.line ? addressObj.line.join(", ") : "-";
  const city = addressObj && addressObj.city ? addressObj.city : "-";
  const state = addressObj && addressObj.state ? addressObj.state : "-";
  const postalCode =
    addressObj && addressObj.postalCode ? addressObj.postalCode : "-";
  return [addressLine, city, state, postalCode];
};

const scoreObjectHandler = recordDetails => {
  const scoreRecord = {};
  scoreRecord.scoreId = recordDetails.id ? recordDetails.id : "";
  scoreRecord.scoreString = recordDetails.scoreString
    ? recordDetails.scoreString
    : "";
  scoreRecord.recordHrn = recordDetails.hrn ? recordDetails.hrn : null;
  scoreRecord.actions = recordDetails.score;
  scoreRecord.recordNumberInGoldenId =
    recordDetails.table === "primary"
      ? recordDetails.recordNumberInGoldenId
      : null;
  return elementScoreHandler(scoreRecord, recordDetails.scoreString);
};

export const records = (identities, sources, nameToTypeMap) => {
  const transformedRecordsArray = [];
  let recordHistoryCounter = 0;
  for (let identity of identities) {
    let recordList;

    if (identity.table !== "historyTable") {
      recordList = identity.records != null ? identity.records : identity;
    } else {
      recordList = recordHistoryCounter === 0 ? identities : [];
      recordHistoryCounter++;
    }
    for (let record of recordList) {
      const rec = {};
      rec.subRows = [];
      rec.id = `${record.id}+${record.meta.source}`;
      rec.sourceEntityId = record.id;
      rec.source = record.meta.source;
      rec.sourceName =
        sources && sources[record.meta.source]
          ? sources[record.meta.source]
          : record.meta.source;
      rec.lastupdated = record.meta.lastUpdated;
      rec.moreoptions = false;
      rec.recordId = record.id;
      if (identity.table !== "historyTable") {
        rec.goldenId = identity.id;
        rec.goldenIdNumber =
          identity.table === "secondary"
            ? identities.findIndex(ele => ele.id === identity.id) + 2
            : identities.findIndex(ele => ele.id) + 1;
        rec.recordNumberInGoldenId =
          recordList.findIndex(ele => ele.id === rec.recordId) + 1;
      }
      rec.isExpanded = false;
      rec.excludedBy = record.excludedBy;
      rec.identitySpecID = record.identitySpecID;
      rec.table = identity.table;
      // rec.table = identity.records ? "primary" : "secondary";

      rec.birthDate = record.birthDate
        ? moment(record.birthDate).format("L")
        : "-";

      rec.deceasedDateTime = record.deceasedDateTime
        ? moment(record.deceasedDateTime).format("L")
        : record.deceasedDate
        ? moment(record.deceasedDate).format("L")
        : "-";
      rec.nameUse =
        record.name && record.name[0].use ? record.name[0].use : "-";
      rec.addressUse =
        record.address && record.address[0].use ? record.address[0].use : "-";
      rec.emailUse = record.telecom ? emailHandler(record.telecom)[1] : "-";
      rec.phoneUse = record.telecom
        ? phoneNumberHandler(record.telecom)[1]
        : "-";
      rec.gender = record.gender ? record.gender : "-";
      rec.suffixName = nameHandler(record.name)[0];
      rec.givenName = nameHandler(record.name)[1];
      rec.familyName = nameHandler(record.name)[2];
      if (record.address) {
        if (record.address.length > 1) {
          const addresses = record.address.slice(1);
          addresses.forEach(address =>
            rec.subRows.push({ address: addressHandler(address) })
          );
        }
        rec.addressLine = multipleAddressHandler(record.address)[0];
        rec.city = multipleAddressHandler(record.address)[1];
        rec.state = multipleAddressHandler(record.address)[2];
        rec.postalCode = multipleAddressHandler(record.address)[3];
      } else {
        rec.address = "";
        rec.addressLine = "-";
        rec.city = "-";
        rec.state = "-";
        rec.postalCode = "-";
      }
      rec.email = record.telecom ? emailHandler(record.telecom)[0] : "-";
      rec.phone = record.telecom ? phoneNumberHandler(record.telecom)[0] : "-";
      identifiers.forEach(idt => {
        rec[idt.name] = record.identifier
          ? identifierHandler(record.identifier, idt.system, idt.code)
          : "-";
      });
      rec.score = record.score !== "" ? record.score : "";
      rec.scoreString = record.scoreString !== "" ? record.scoreString : "";
      rec.hrn = record.hrn ? record.hrn : "-";
      rec.identifier = record.identifier;
      rec.addressCount = record.address ? record.address.length : 0;
      rec.nameCount = record.name ? record.name.length : 0;
      rec.emailCount = record.telecom ? emailCountHandler(record.telecom) : 0;
      rec.phoneCount = record.telecom
        ? phoneNumberCountHandler(record.telecom)
        : 0;
      rec.identifierCount = record.identifier ? record.identifier.length : 0;
      rec.eimpLastUpdated = record.eimpLastUpdated;

      rec.lastUpdatedTime = record.lastUpdatedTime;
      rec.linkReferenceId =
        record.link && record.link.length > 0
          ? record.link[0].other.reference
          : "";
      rec.household = "-";
      rec.identifiers = "-";
      rec.isQuarantine = record.classification ? true : false;
      rec.quarantineType = record.classification
        ? nameToTypeMap
          ? nameToTypeMap[record.classification]["type"]
          : "-"
        : "-";
      rec.hasOverride = recordHasOverride(record);
      rec.quarantineReason = record.reason ? record.reason : "-";
      const data = record.identifier
        ? record.identifier.filter(identifiers => {
            return (
              "type" in identifiers &&
              "coding" in identifiers.type &&
              identifiers.type.coding.some(c => c.code === "COV") &&
              identifiers.use !== "old"
            );
          })
        : [];
      let coverageValue = {};
      try {
        coverageValue =
          data.length > 0 && data[0].value ? JSON.parse(data[0].value) : {};
      } catch (e) {
        console.log("Not a coverage identity", data, e);
      }

      const covEndDate1 = record.extension
        ? record.extension.filter(e => e.url.includes("coverageEndDate"))
        : [];
      const covDate =
        covEndDate1.length > 0 && covEndDate1[0] && covEndDate1[0].valueDateTime
          ? formattedDateWithDefault(covEndDate1[0].valueDateTime)
          : covEndDate1.length > 0 &&
            covEndDate1[0] &&
            covEndDate1[0].valueString
          ? formattedDateWithDefault(covEndDate1[0].valueString)
          : "-";
      rec.policyNum = coverageValue.policyNum || "-";
      rec.dependentCd = coverageValue.dependentCd || "-";
      rec.divisionCd = coverageValue.divisionCd || "-";
      rec.relationshipCd = coverageValue.relationshipCd || "-";
      rec.familyID = coverageValue.familyID || "-";
      rec.memberID = coverageValue.memberID || "-";
      rec.subscriberID = coverageValue.subscriberID || "-";
      rec.carrierID = coverageValue.carrierID || "-";
      rec.accountID = coverageValue.accountID || "-";
      rec.groupID = coverageValue.groupID || "-";
      rec.origSysId = coverageValue.origSysId || "-";
      rec.medicaidRN = coverageValue.medicaidRN || "-";
      rec.cdbXrefId = identifierHandler(record.identifier, "cdbXrefId", "");
      rec.compasMemberNumber = identifierHandler(
        record.identifier,
        "compasMemberNumber",
        ""
      );
      rec.employerAssignedId = identifierHandler(
        record.identifier,
        "",
        "employerAssignedId"
      );
      rec.irisAccountNumber = identifierHandler(
        record.identifier,
        "irisAccountNumber",
        ""
      );
      rec.niceMemberId = identifierHandler(
        record.identifier,
        "niceMemberId",
        ""
      );
      rec.oxfordMemberId = identifierHandler(
        record.identifier,
        "oxfordMemberId",
        ""
      );
      rec.rxClaimsMemberId = identifierHandler(
        record.identifier,
        "rxClaimsMemberId",
        ""
      );
      rec.rxClaimsSupplementalId = identifierHandler(
        record.identifier,
        "rxClaimsSupplementalId",
        ""
      );
      rec.riverValleyMemberId = identifierHandler(
        record.identifier,
        "riverValleyMemberId",
        ""
      );
      rec.scriptMedPatientId = identifierHandler(
        record.identifier,
        "scriptMedPatientId",
        ""
      );
      rec.ursulaId = identifierHandler(record.identifier, "ursulaId", "");
      rec.studentResourcesId = identifierHandler(
        record.identifier,
        "studentResourcesId",
        ""
      );
      rec.altID = identifierHandler(record.identifier, "", "AltID");
      rec.coverageEndDate = covDate;

      // Helper to collect all audit extensions from root and attribute-level arrays
      function getAllAuditExtensions(record) {
        let allExt = [];
        // Root-level
        if (record.extension) allExt = allExt.concat(record.extension);
        // Attribute-level
        ["address", "name", "telecom", "identifier"].forEach(attr => {
          if (record[attr] && Array.isArray(record[attr])) {
            record[attr].forEach(item => {
              if (item.extension) allExt = allExt.concat(item.extension);
            });
          }
        });
        return allExt;
      }

      // List all possible audit URLs
      const allOverriddenByUrls = [
        overriddenByString,
        dobOverriddenByString,
        genderOverriddenByString,
        multipleBirthOverriddenByString
      ];
      const allOverrideReasonUrls = [
        overrideReasonString,
        dobOverrideReasonString,
        genderOverrideReasonString,
        multipleBirthOverrideReasonString
      ];
      const allOverrideTimestampUrls = [
        overrideTimestampString,
        dobOverrideTimestampString,
        genderOverrideTimestampString,
        multipleBirthOverrideTimestampString
      ];

      // Collect all audit extensions
      const allAuditExts = getAllAuditExtensions(record);

      // Find all overrideTimestamp extensions and their values (with their index in allAuditExts)
      const allTimestamps = [];
      allAuditExts.forEach((ext, idx) => {
        if (allOverrideTimestampUrls.includes(ext.url) && ext.valueString) {
          allTimestamps.push({ value: ext.valueString, url: ext.url, idx });
        }
      });

      // Find the latest overrideTimestamp (by date)
      let latest = null;
      if (allTimestamps.length > 0) {
        latest = allTimestamps.reduce((a, b) =>
          new Date(a.value) > new Date(b.value) ? a : b
        );
      }

      // Convert rec.lastUpdatedTime (epoch) to GMT ISO string
      let lastUpdatedTimeGMT = "";
      if (rec.lastUpdatedTime) {
        lastUpdatedTimeGMT = new Date(
          Number(rec.lastUpdatedTime)
        ).toISOString();
      }

      // Compare timestamps (allowing up to 10s difference)
      let showAudit = false;
      if (latest && lastUpdatedTimeGMT) {
        const diff = Math.abs(
          new Date(latest.value).getTime() -
            new Date(lastUpdatedTimeGMT).getTime()
        );
        if (diff <= 10000) {
          showAudit = true;
        }
      }

      // If timestamps match, get corresponding overriddenBy and overrideReason from the same attribute group
      if (showAudit) {
        // Find the attribute group for this timestamp
        // Look for siblings in the same array slice (root or attribute-level)
        let siblings = [];
        // Try to find in root-level
        if (
          record.extension &&
          record.extension.includes(allAuditExts[latest.idx])
        ) {
          siblings = record.extension;
        } else {
          // Try attribute-level
          ["address", "name", "telecom", "identifier"].some(attr => {
            if (record[attr] && Array.isArray(record[attr])) {
              return record[attr].some(item => {
                if (
                  item.extension &&
                  item.extension.includes(allAuditExts[latest.idx])
                ) {
                  siblings = item.extension;
                  return true;
                }
                return false;
              });
            }
            return false;
          });
        }
        // Find the correct overriddenBy and overrideReason in siblings
        const overriddenBy = siblings.find(
          ext => allOverriddenByUrls.includes(ext.url) && ext.valueString
        );
        const overrideReason = siblings.find(
          ext => allOverrideReasonUrls.includes(ext.url) && ext.valueString
        );
        rec.overriddenBy = overriddenBy ? overriddenBy.valueString : "-";
        rec.overrideReason = overrideReason ? overrideReason.valueString : "-";
        rec.action = "Override";
      } else {
        rec.overriddenBy = "-";
        rec.overrideReason = "-";
        rec.action = "-";
      }
      transformedRecordsArray.push(rec);
      if (rec.table === "primary") {
        const scoreRecord = scoreObjectHandler(rec);
        transformedRecordsArray.push(scoreRecord);
      }
    }
    if (identity.hasOwnProperty("table") && identity.table === "secondary") {
      const relatedIdentityScoreRow = scoreObjectHandler(identity);
      transformedRecordsArray.push(relatedIdentityScoreRow);
    }
  }
  return transformedRecordsArray;
};

export default (identities, sources) => {
  const transformedRecordsArray = [];
  const sourceMap = new Map();
  for (const source of sources) {
    sourceMap.set(source.oid, source.name);
  }
  identities.forEach((identity, index) => {
    const records = identity.records;
    for (let record of records) {
      const rec = {};
      rec.subRows = [];
      rec.id = `${record.id}+${record.meta.source}`;
      rec.gid = identity.id;
      rec.source = record.meta.source;
      rec.sourceName = sourceMap.get(record.meta.source)
        ? sourceMap.get(record.meta.source)
        : "-";
      rec.recordId = record.id;
      rec.excludedBy = record.excludedBy;
      rec.identitySpecID = record.identitySpecID;
      rec.category = identity.category;
      rec.birthDate = record.birthDate ? record.birthDate : "-";
      rec.deceasedDateTime = record.deceasedDateTime
        ? moment(record.deceasedDateTime).format("L")
        : record.deceasedDate
        ? moment(record.deceasedDate).format("L")
        : "-";
      rec.gender = record.gender ? record.gender : "-";
      rec.name = record.name ? fullNameHandler(record.name[0]) : "-";
      if (record.address) {
        if (record.address.length > 1) {
          const addresses = record.address.slice(1);
          addresses.forEach(address =>
            rec.subRows.push({ address: addressHandler(address) })
          );
        }
        rec.address = addressHandler(record.address[0]);
      } else {
        rec.address = "";
      }
      rec.email = record.telecom ? emailHandler(record.telecom) : "-";
      rec.phone = record.telecom ? phoneNumberHandler(record.telecom) : "-";
      identifiers.forEach(idt => {
        rec[idt.name] = record.identifier
          ? identifierHandler(record.identifier, idt.system)
          : "-";
      });
      // rec.mrn = record.identifier ? mrnHandler(record.identifier) : "-";
      // rec.mrn = "mrn";
      transformedRecordsArray.push(rec);
    }
  });

  return transformedRecordsArray;
};
